import React, { useState, useRef, useEffect, useCallback } from 'react'
import { useAuth } from '../hooks/useAuth'
import { useNavigate } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
    faLock,
    faCircleNotch,
    faArrowRightFromBracket,
    faShieldHalved,
    faCheckCircle,
} from '@fortawesome/free-solid-svg-icons'

const SessionLockScreen: React.FC = () => {
    const { user, isSessionExpired, reAuthenticate, logout, dismissSessionExpired } =
        useAuth()
    const navigate = useNavigate()

    const [password, setPassword] = useState('')
    const [isAuthenticating, setIsAuthenticating] = useState(false)
    const [error, setError] = useState('')
    const [success, setSuccess] = useState(false)
    const [visible, setVisible] = useState(false)
    const passwordRef = useRef<HTMLInputElement>(null)

    useEffect(() => {
        if (isSessionExpired && user) {
            requestAnimationFrame(() => setVisible(true))
            setTimeout(() => passwordRef.current?.focus(), 600)
        } else {
            setVisible(false)
            setPassword('')
            setError('')
            setSuccess(false)
        }
    }, [isSessionExpired, user])

    const handleSubmit = useCallback(
        async (e: React.FormEvent) => {
            e.preventDefault()
            if (!password.trim() || isAuthenticating) return

            setError('')
            setIsAuthenticating(true)

            try {
                const ok = await reAuthenticate(password)
                if (ok) {
                    setSuccess(true)
                    setTimeout(() => {
                        setVisible(false)
                        setPassword('')
                        setSuccess(false)
                    }, 800)
                } else {
                    setError('Invalid password. Please try again.')
                    setPassword('')
                    passwordRef.current?.focus()
                }
            } catch {
                setError('Authentication failed. Please try again.')
                setPassword('')
            } finally {
                setIsAuthenticating(false)
            }
        },
        [password, isAuthenticating, reAuthenticate]
    )

    const handleFullLogout = useCallback(async () => {
        dismissSessionExpired()
        await logout()
        navigate('/login')
    }, [logout, navigate, dismissSessionExpired])

    if (!isSessionExpired || !user) return null

    return (
        <div
            className={`session-lock ${visible ? 'session-lock--visible' : ''}`}
        >
            <div className="session-lock__backdrop" />

            <div className="session-lock__particles">
                {Array.from({ length: 20 }).map((_, i) => (
                    <div
                        key={i}
                        className="session-lock__particle"
                        style={
                            {
                                '--delay': `${Math.random() * 8}s`,
                                '--x': `${Math.random() * 100}%`,
                                '--duration': `${6 + Math.random() * 8}s`,
                                '--size': `${2 + Math.random() * 4}px`,
                            } as React.CSSProperties
                        }
                    />
                ))}
            </div>

            <div
                className={`session-lock__card ${success ? 'session-lock__card--success' : ''}`}
            >
                <div className="session-lock__shield">
                    <div className="session-lock__shield-ring" />
                    <div className="session-lock__shield-ring session-lock__shield-ring--delayed" />
                    <div className="session-lock__shield-icon">
                        {success ? (
                            <FontAwesomeIcon
                                icon={faCheckCircle}
                                className="session-lock__icon-success"
                            />
                        ) : (
                            <FontAwesomeIcon icon={faShieldHalved} />
                        )}
                    </div>
                </div>

                <div className="session-lock__avatar-section">
                    {user.image_id && user.image ? (
                        <img
                            src={user.image}
                            alt={user.username}
                            className="session-lock__avatar"
                        />
                    ) : (
                        <div className="session-lock__avatar-placeholder">
                            {(user.name || user.username || 'U')
                                .charAt(0)
                                .toUpperCase()}
                        </div>
                    )}
                    <h2 className="session-lock__username">
                        {user.name || user.username}
                    </h2>
                </div>

                <div className="session-lock__message">
                    <FontAwesomeIcon
                        icon={faLock}
                        className="session-lock__lock-icon"
                    />
                    <span>Session Locked</span>
                </div>
                <p className="session-lock__subtitle">
                    Your session has expired for security. Enter your password to
                    continue where you left off.
                </p>

                <form
                    className="session-lock__form"
                    onSubmit={handleSubmit}
                >
                    <div className="session-lock__input-group">
                        <input
                            ref={passwordRef}
                            type="password"
                            className={`session-lock__input ${error ? 'session-lock__input--error' : ''}`}
                            placeholder="Enter your password"
                            value={password}
                            onChange={(e) => {
                                setPassword(e.target.value)
                                setError('')
                            }}
                            disabled={isAuthenticating || success}
                            autoComplete="current-password"
                        />
                        <div className="session-lock__input-border" />
                    </div>

                    {error && (
                        <div className="session-lock__error">{error}</div>
                    )}

                    <button
                        type="submit"
                        className="session-lock__submit"
                        disabled={
                            isAuthenticating || !password.trim() || success
                        }
                    >
                        {isAuthenticating ? (
                            <>
                                <FontAwesomeIcon
                                    icon={faCircleNotch}
                                    spin
                                />
                                <span>Unlocking...</span>
                            </>
                        ) : success ? (
                            <>
                                <FontAwesomeIcon icon={faCheckCircle} />
                                <span>Welcome back!</span>
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon={faLock} />
                                <span>Unlock Session</span>
                            </>
                        )}
                    </button>
                </form>

                <button
                    type="button"
                    className="session-lock__logout"
                    onClick={handleFullLogout}
                    disabled={isAuthenticating}
                >
                    <FontAwesomeIcon icon={faArrowRightFromBracket} />
                    <span>Sign out instead</span>
                </button>
            </div>
        </div>
    )
}

export default SessionLockScreen
